import React from 'react';
import { Col, Typography } from 'antd';
import { MULTI_DEPT_BUDGET_ORG_CODE } from './PrepaymentForm';
import { findOrgDisplayName } from './PrepaymentReadonlyInfo';
import CompactTableAttachments from './CompactTableAttachments';

const { Text } = Typography;

export const FINANCE_READONLY_GUTTER = [16, 8];

export function formatFinanceAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `¥${num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function hasFinanceReadonlyValue(value) {
  if (value == null) return false;
  if (typeof value === 'object') return true;
  const text = String(value).trim();
  return text !== '' && text !== '—';
}

export function resolvePrimaryBudgetCategoryName(formData = {}) {
  const categoryName = String(formData.category_name || '').trim();
  if (categoryName) return categoryName;
  const label = String(formData.budget_subject_label || '').trim();
  if (!label) return '—';
  const slashIdx = label.indexOf(' / ');
  return slashIdx >= 0 ? label.slice(0, slashIdx).trim() : label;
}

export function FinanceReadonlyItem({ label, value, span = 8, hideWhenEmpty = false }) {
  if (hideWhenEmpty && !hasFinanceReadonlyValue(value)) return null;
  return (
    <Col xs={24} md={span}>
      <div className="prepayment-readonly-item">
        <Text type="secondary" className="prepayment-readonly-label">{label}</Text>
        <div className="prepayment-readonly-value">{value ?? '—'}</div>
      </div>
    </Col>
  );
}

export function buildExpenseClaimLineRows(lines = [], organizations = []) {
  const rows = [];
  (lines || []).forEach((line, index) => {
    const isMulti = String(line?.budget_org_code || '').trim() === MULTI_DEPT_BUDGET_ORG_CODE;
    if (isMulti && Array.isArray(line.budget_splits) && line.budget_splits.length) {
      line.budget_splits.forEach((split, splitIndex) => {
        rows.push({
          key: `${index}-${splitIndex}`,
          lineNo: index + 1,
          budget_org_name: findOrgDisplayName(organizations, split.org_code, split.org_name),
          subitem_name: line.subitem_name || '—',
          expense_detail: line.expense_detail || '—',
          invoice_type_label: line.invoice_type_label || line.invoice_type || '—',
          invoice_no: line.invoice_no || '—',
          invoice_amount: split.amount ?? line.invoice_amount,
          actual_amount: split.amount ?? line.actual_amount,
          invoice_files: splitIndex === 0 ? (line.invoice_files || []) : []
        });
      });
      return;
    }
    rows.push({
      key: String(index),
      lineNo: index + 1,
      budget_org_name: findOrgDisplayName(
        organizations,
        line.budget_org_code || line.cost_org_code,
        line.budget_org_name
      ),
      subitem_name: line.subitem_name || '—',
      expense_detail: line.expense_detail || '—',
      invoice_type_label: line.invoice_type_label || line.invoice_type || '—',
      invoice_no: line.invoice_no || '—',
      invoice_amount: line.invoice_amount,
      actual_amount: line.actual_amount,
      invoice_files: line.invoice_files || []
    });
  });
  return rows;
}

export function buildWriteoffInvoiceLineRows(lines = []) {
  return (lines || []).map((line, index) => ({
    key: String(index),
    lineNo: index + 1,
    expense_detail: line.expense_detail || '—',
    invoice_type_label: line.invoice_type_label || line.invoice_type || '—',
    invoice_no: line.invoice_no || '—',
    invoice_amount: line.invoice_amount,
    invoice_files: line.invoice_files || []
  }));
}

export function createExpenseLineTableColumns() {
  return [
    { title: '序号', dataIndex: 'lineNo', width: 56, fixed: 'left' },
    { title: '费用归属', dataIndex: 'budget_org_name', width: 110, ellipsis: true },
    { title: '预算二级科目', dataIndex: 'subitem_name', width: 100, ellipsis: true },
    { title: '费用明细', dataIndex: 'expense_detail', width: 100, ellipsis: true },
    { title: '发票类型', dataIndex: 'invoice_type_label', width: 72, ellipsis: true },
    { title: '发票号', dataIndex: 'invoice_no', width: 120, ellipsis: true },
    {
      title: '发票金额',
      dataIndex: 'invoice_amount',
      width: 96,
      align: 'right',
      render: (v) => formatFinanceAmount(v)
    },
    {
      title: '实付金额',
      dataIndex: 'actual_amount',
      width: 96,
      align: 'right',
      render: (v) => formatFinanceAmount(v)
    },
    {
      title: '发票附件',
      dataIndex: 'invoice_files',
      width: 88,
      render: (files) => <CompactTableAttachments attachments={files} />
    }
  ];
}

export function createWriteoffInvoiceTableColumns() {
  return [
    { title: '序号', dataIndex: 'lineNo', width: 56, fixed: 'left' },
    { title: '费用明细', dataIndex: 'expense_detail', width: 100, ellipsis: true },
    { title: '发票类型', dataIndex: 'invoice_type_label', width: 72, ellipsis: true },
    { title: '发票号', dataIndex: 'invoice_no', width: 120, ellipsis: true },
    {
      title: '发票金额',
      dataIndex: 'invoice_amount',
      width: 96,
      align: 'right',
      render: (v) => formatFinanceAmount(v)
    },
    {
      title: '发票附件',
      dataIndex: 'invoice_files',
      width: 88,
      render: (files) => <CompactTableAttachments attachments={files} />
    }
  ];
}

export function countLineAttachments(lines = []) {
  return (lines || []).reduce(
    (sum, line) => sum + (Array.isArray(line.invoice_files) ? line.invoice_files.length : 0),
    0
  );
}
